import React, { useEffect, useState } from "react";
import "./HistoryImport.css";
import {
  MdOutlineKeyboardArrowDown,
  MdOutlineKeyboardArrowUp,
  MdOutlineFileDownload,
} from "react-icons/md";

import { fetchResultsData, downloadResultsData } from "./ResultsDisplay";
import { ToastContainer ,toast } from "react-toastify";
import { useNavigation } from "../../../../contexts/NavigationContext";
import {simulationService} from "../../../../services/api/simulationService";
import { PYTHON_API_BASE_URL } from "../../../../services/api/apiConstants";

// Helper to sanitize an email
const formatEmailForKey = (email) => {
  return email.replace(/[@.]/g, "-");
}

// Extracts the user’s simulation results from the JSON
const getAllResultsFromResponse = (jsonData) => {
  const selectedCustomerEmail = sessionStorage.getItem("selectedCustomerEmail");
  if (!selectedCustomerEmail) return [];
  const formattedEmail = formatEmailForKey(selectedCustomerEmail);
  const results = [];
  const simulationNames = [];

  for (const key in jsonData) {
    if (key.startsWith(formattedEmail)) {
      simulationNames.push(key);
     
      const entry = jsonData[key];
      
      
      if (entry.results && Array.isArray(entry.results)) {
        const enrichedResults = entry.results.map((res) => ({
          ...res,
          simulationName: key, // Attach the simulation/container name
        }));
        results.push(...enrichedResults); // Merge eall results arrays
      }
    }
  }

  return results;
}

const extractMeterNameFromFile = (fileName) => {
  const fileOnly = fileName.split("/").pop(); // handles paths
  const match = fileOnly.match(/^(.+?)-\d{4}-\d{2}-\d{2}/);
  return match ? match[1] : null;
}

const groupResultsByLast4Digits = (resultsArray) => {
  const grouped = []; // valid timeseries-dailySummary pairs
  const timeseriesFiles = [];
  const dailySummaryFiles = [];
  const meterMap = {}; // container::meter → files

  resultsArray.forEach((fileObj) => {
    const { name, simulationName } = fileObj;
    const parsedTime = extractTimeFromName(name);
    if (!parsedTime) return;

    const fileWithTime = { ...fileObj, parsedTime, simulationName };

    if (name.includes("-timeseries-")) {
      timeseriesFiles.push(fileWithTime);

      // Add to meterMap
      const meterName = extractMeterNameFromFile(name);
      if (meterName) {
        const key = `${simulationName}::${meterName}`;
        if (!meterMap[key]) {
          meterMap[key] = {
            simulationName,
            meterName,
            files: [],
          };
        }
        meterMap[key].files.push(fileWithTime);
      }
    } else if (name.includes("daily-summary-")) {
      dailySummaryFiles.push(fileWithTime);
    }
  });
  

  // Match timeseries ↔ daily summary (±1 minute)
  timeseriesFiles.forEach((tsFile) => {
    const match = dailySummaryFiles.find(
      (dsFile) =>
        Math.abs(new Date(tsFile.parsedTime) - new Date(dsFile.parsedTime)) <=
          60000 && dsFile.simulationName === tsFile.simulationName,
    );

    if (match) {
      grouped.push({
        timeKey: tsFile.name.slice(-8, -4),
        createdAt: tsFile.created,
        timeseries: { ...tsFile },
        dailySummary: { ...match },
        simulationName: tsFile.simulationName,
      });
    }
  });

  // Sort grouped pairs by createdAt DESC
  grouped.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
 

  // Convert meterMap to simulation → meters → files structure
  const simulationMap = {};

  grouped.forEach((pair) => {
    const simulationName = pair.simulationName;
    const meterName = extractMeterNameFromFile(pair.timeseries.name); // from your earlier function

    const simKey = simulationName;
    const meterKey = `${simulationName}::${meterName}`;

    if (!simulationMap[simKey]) {
      simulationMap[simKey] = {
        simulationName: simKey,
        meters: [],
      };
    }

    let meterEntry = simulationMap[simKey].meters.find(
      (m) => m.meterName === meterName,
    );
    if (!meterEntry) {
      meterEntry = {
        meterName,
        files: [],
      };
      simulationMap[simKey].meters.push(meterEntry);
    }

    // Push timeseries + dailySummary file
    meterEntry.files.push({
      timeKey: pair.timeKey,
      createdAt: pair.createdAt,
      timeseries: pair.timeseries,
      dailySummary: pair.dailySummary,
    });
  });

  const groupedByMeter = Object.values(simulationMap).sort((a, b) => {
    const latestA = new Date(
      Math.max(
        ...a.meters.flatMap((m) => m.files.map((f) => new Date(f.createdAt))),
      ),
    );
    const latestB = new Date(
      Math.max(
        ...b.meters.flatMap((m) => m.files.map((f) => new Date(f.createdAt))),
      ),
    );
    return latestB - latestA;
  });
  
  
  return {
    groupedByMeter, // container → meters → files
    flatSortedList: grouped, // flat valid results list
  };
}

const extractTimeFromName = (name) => {
  const match = name.match(/(\d{6}_\d{4})\.csv$/); // Matches both old & new
  if (!match) return null;

  const [mmddyy, hhmm] = match[1].split("_");
  const year = "20" + mmddyy.slice(4);
  const month = mmddyy.slice(0, 2);
  const day = mmddyy.slice(2, 4);
  const hour = hhmm.slice(0, 2);
  const minute = hhmm.slice(2, 4);

  return new Date(`${year}-${month}-${day}T${hour}:${minute}:00`);
}

/**
 * Groups CSV file objects by last 4 digits in the filenames.
 * Only includes pairs: (economic_summary_XXXX.csv, timeseries_XXXX.csv).
 * Sorts them by creation date descending (newest first).
 */

// Basic CSV->JSON parser
const csvToJson = (csvText) => {
  const lines = csvText.split("\n").filter((line) => line.trim());
  if (!lines.length) return [];
  const headers = lines[0].split(",").map((h) => h.trim());
  return lines.slice(1).map((line) => {
    const values = line.split(",").map((v) => v.trim());
    const obj = {};
    headers.forEach((header, index) => {
      obj[header] = values[index] || "";
    });
    return obj;
  });
}

// Add this new function near the top with other helper functions
const loadSimulationData = async (
  simulation,
  {
    baseUrl,
    storageAccountName,
    simulationName,
    setSelectedCSVData,
    setTimeseriesFile,
    setTriggerProcessing,
    setProcessingSimulationTimeKey,
  },
) => {

  const token = sessionStorage.getItem("accessToken");
  if (!token || !simulation?.timeseries?.name) return;

 

  try {
    const csvText = await fetchResultsData(
      "Timeseries",
      simulation.timeseries.name,
      baseUrl,
      storageAccountName,
      simulation.simulationName,
      token,
      "from historyImport loadSimulation",
    );

    if (!csvText || csvText.trim() === "") {
      throw new Error("Received empty CSV data from server.");
    }

   
    setTimeseriesFile(simulation.timeseries.name);
    setSelectedCSVData(csvText);
    setTriggerProcessing(true);
    //making the processingtimekey as  null by default
  } catch (err) {
    toast.error("[HistoryImport] Error loading simulation data:", err);
  }
}

export default function HistoryImport() {
  // The list of simulations
  const [simulations, setSimulations] = useState([]);
  const [expandedRow, setExpandedRow] = useState(null);
  const [error, setError] = useState(null);

  // Track which run is "current"
  const [currentSimulationTimeKey, setCurrentSimulationTimeKey] =
    useState(null);
  // The creation date of the newest run we have EVER seen
  const [maxKnownCreationDate, setMaxKnownCreationDate] = useState(null);

  // For DataProcessor
  const [selectedCSVData, setSelectedCSVData] = useState(null);
  const [triggerProcessing, setTriggerProcessing] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [processingSimulationTimeKey, setProcessingSimulationTimeKey] =
    useState(null);
  const [processingComplete, setProcessingComplete] = useState(false);

  // Additional
  const [simulationFailureMessage, setSimulationFailureMessage] = useState("");
  const [simulationInProgress, setSimulationInProgress] = useState(false);
  const [timeseriesFile, setTimeseriesFile] = useState("");
  const [timeseriesFileData, setTimeseriesFileData] = useState([]);

  // For inline CSV previews in the expanded row
  const [previewData, setPreviewData] = useState({});

  // Possibly for modals, etc.
  const [viewContent, setViewContent] = useState("");
  const [viewTitle, setViewTitle] = useState("");
  const [expandedSimulations, setExpandedSimulations] = useState({});
  const [expandedMeters, setExpandedMeters] = useState({});

  // If you have a spinner
  const [isLoading, setIsLoading] = useState(true);

  // Basic environment config
  
  const baseUrl = process.env.REACT_APP_BASE_URL;
  const storedKey = sessionStorage.getItem("currentSimulationTimeKey") || null;
  const storedMaxDate = sessionStorage.getItem("maxKnownCreationDate") || null;
  // The user’s storage account name
  const [storageAccountName, setStorageAccountName] = useState("");

  const [visibleSeriesKey, setVisibleSeriesKey] = useState(null);
  const [visibleDailyserieskey, setVisibleDailySerieskey] = useState(null);

  const [csvFileNmae, setCsvFileName] = useState("");
  const { timeSeriesFileName, setTimeSeriesFileName, selectedBranchId } = useNavigation();

  //
  // The sanitized simulation name
  const simulationName =
    sessionStorage.getItem("selectedCustomerEmail")?.replace(/[@.]/g, "-") ||
    "default-simulation-name";

  // For uniform buttons
  const uniformButtonStyle = {
    minWidth: "150px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  };
  const toggleSimulation = (simKey) => {
    setExpandedSimulations((prev) => ({
      ...prev,
      [simKey]: !prev[simKey],
    }));
  };

  const toggleMeter = (simKey, meterName) => {
    const key = `${simKey}-${meterName}`;
    setExpandedMeters((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };
  const customer = simulationName;

  // 1) On mount, retrieve 'currentSimulationTimeKey' and 'maxKnownCreationDate'
  useEffect(() => {
    if (storedKey) {
      setCurrentSimulationTimeKey(storedKey);
    }
    if (storedMaxDate) {
      setMaxKnownCreationDate(storedMaxDate);
    }
  }, []);

  // 2) Fetch the user’s storage account name
  const fetchStorageAccountName = async (branchId) => {
    const API_URL = `${PYTHON_API_BASE_URL}/storage-account/?branch_id=${branchId}`;
    try {
      const accessToken = sessionStorage.getItem("accessToken");
      const response = await fetch(API_URL, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
          "X-User-ID": sessionStorage.getItem("UserID") || "",
        },
      });
      if (!response.ok) {
        throw new Error("Failed to fetch storage account name.");
      }
      const data = await response.json();
      if (data.storage_account) {
        setStorageAccountName(data.storage_account.storage_account_name);
       
      } else {
       
      }
    } catch (error) {
      toast.error("Error fetching storage account name:", error);
      setStorageAccountName("");
    }
  };

  // 3) On mount, load the user’s storage account
  useEffect(() => {
    if (selectedBranchId) {
      fetchStorageAccountName(selectedBranchId);
    } else {
      console.error("No branch selected in navigation context.");
    }
  }, []);

  // 4) Once we have the storageAccountName, fetch the simulation listing
  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);

        const accessToken = sessionStorage.getItem("accessToken");
        if (!accessToken) {
          throw new Error(
            "No accessToken found in session storage! Please log in.",
          );
        }
        const customerName = storageAccountName;
       
       const data = await simulationService.getList({ customerName, accessToken });
      

        // Convert to array of results
        const allResults = getAllResultsFromResponse(data);
        
        

        // Group into simulation objects
        const { groupedByMeter, flatSortedList } =
          groupResultsByLast4Digits(allResults);
        
        
        setSimulations(groupedByMeter);

        const storedFileName = sessionStorage.getItem("timeSeriesFileName");
        const storedCreatedAt = sessionStorage.getItem("lastSelectedCreatedAt");

        // If we have at least one simulation
      
        if (flatSortedList.length > 0) {
          const latest = flatSortedList[0];
          const newestCreatedAt = new Date(latest.createdAt);
          const newest = groupedByMeter[0];
          const newestsimulationName = newest.simulationName;
         

          const isNewer =
            !storedCreatedAt || newestCreatedAt > new Date(storedCreatedAt);
         
          if (isNewer) {
            // Automatically load the newest simulation's data
            await loadSimulationData(newest, {
              baseUrl,
              storageAccountName,
              newestsimulationName,
              setSelectedCSVData,
              setTimeseriesFile,
              setTriggerProcessing,
              setProcessingSimulationTimeKey,
            });

          

            if (
              storedMaxDate == null ||
              newestCreatedAt > new Date(storedMaxDate)
            ) {
              setMaxKnownCreationDate(newest.createdAt);
              setCurrentSimulationTimeKey(newest.timeKey);
              
              setTimeSeriesFileName(timeSeriesFileName);
              sessionStorage.setItem(
                "currentSimulationTimeKey",
                newest.timeKey,
              );
              sessionStorage.setItem("maxKnownCreationDate", newest.createdAt);
            }

            
          }
        }

        setIsLoading(false);
      } catch (err) {
        toast.error("[HistoryImport] Error:", err);
        setError(err.message);
        setIsLoading(false);
      }
    }

    if (storageAccountName) {
      fetchData();
    }
  }, [baseUrl, storageAccountName, simulationName]);

 

  // Figure out file type
  const getFileType = (fileName) => {
    if (fileName.startsWith("timeseries_")) return "Timeseries";
    return "Daily Summary";
  };

  // Download
  const handleDownloadHistory = async (fileObj, simulationName) => {
    if (!fileObj) return;
    try {
      const token = sessionStorage.getItem("accessToken");
      const type = getFileType(fileObj.name);
      await downloadResultsData(
        type,
        fileObj.name,
        baseUrl,
        storageAccountName,
        simulationName,
        token,
      );
    } catch (err) {
      toast.error("Error downloading file:", err);
      alert(`Error downloading ${fileObj.name}: ${err.message}`);
    }
  };

  

  useEffect(() => {
    if (processingSimulationTimeKey) {
      
      onViewsGenerated(processingSimulationTimeKey);
    }
    
    
  }, [processingSimulationTimeKey]);

  // "Generate Views" => fetch timeseries => run DataProcessor
  const handleGenerateViews = async (simulation) => {
   
    setCsvFileName(simulation.timeseries.name);
    const name = simulation.simulationName;
   
    const token = sessionStorage.getItem("accessToken");
    if (!token) {
      throw new Error("No accessToken found in sessionStorage!");
    }
    const csvText = await fetchResultsData(
      "Timeseries",
      simulation.timeseries.name,
      baseUrl,
      storageAccountName,
      name,
      token,
    );

   
   
    if (!csvText || csvText.trim() === "") {
      throw new Error("Received empty CSV data from server.");
    }

    setProcessingSimulationTimeKey(simulation.timeKey);
    setSimulationInProgress(true);
    setTimeseriesFile(simulation.timeseries.name);
    setTimeSeriesFileName(simulation.timeseries.name);
    setCsvFileName(simulation.timeseries.name);

    sessionStorage.setItem("timeSeriesFileName", simulation.timeseries.name);
    const meterName = simulation.timeseries.name.split("-")[0];
    sessionStorage.setItem("meterName", meterName);
    sessionStorage.setItem("currentSimulationTimeKey", simulation.timeKey);
    sessionStorage.setItem("lastSelectedCreatedAt", simulation.createdAt);
    
    if (csvText) {
      onViewsGenerated(simulation.timeKey, simulation.timeseries.name, csvText);
    }
  };

  // Called when DataProcessor finishes generating new chart data
  const onViewsGenerated = async (timeKey, timeSeriesName, csvText) => {
   
    setTriggerProcessing(false);
    setProcessingComplete(true);
    setSimulationInProgress(false);

   

    if (csvText) {
      setProcessingSimulationTimeKey(null);
      
    }
    // The user explicitly picked a run
    const justProcessedSim = simulations.find((s) => s.timeKey === timeKey);
    if (justProcessedSim && processingSimulationTimeKey == null) {
      setCurrentSimulationTimeKey(justProcessedSim.timeKey);
      sessionStorage.setItem(
        "currentSimulationTimeKey",
        justProcessedSim.timeKey,
      );

      const simDate = new Date(justProcessedSim.createdAt);
      if (!maxKnownCreationDate || simDate > new Date(maxKnownCreationDate)) {
        setMaxKnownCreationDate(justProcessedSim.createdAt);
        sessionStorage.setItem(
          "maxKnownCreationDate",
          justProcessedSim.createdAt,
        );
      }
    }
    setProcessing(false);
   
   
    setSelectedCSVData(null);
  };


 

  // Inline viewing of Timeseries
  const handleViewDailySummary = async (fileObj, timeKey, simulationName) => {
    if (!fileObj) return;
    try {
      const token = sessionStorage.getItem("accessToken");
      const csvText = await fetchResultsData(
        "Daily Summary",
        fileObj.name,
        baseUrl,
        storageAccountName,
        simulationName,
        token,
        "handleViewDailySummary",
      );
      const parsed = csvToJson(csvText);
      setPreviewData((prev) => {
        const current = prev[timeKey] || {};
        return {
          ...prev,
          [timeKey]: {
            ...current,
            dailySummary: current.dailySummary ? null : parsed,
          },
        };
      });
    } catch (err) {
      toast.error("Error viewing daily summary:", err);
      alert(`Error viewing ${fileObj.name}: ${err.message}`);
    }
    setVisibleDailySerieskey((prevKey) =>
      prevKey === timeKey ? null : timeKey,
    );
  };

  
  
  // Inline viewing of Timeseries
  const handleViewTimeseries = async (fileObj, timeKey, simulationName) => {
    
    
    if (!fileObj) return;
    try {
      const token = sessionStorage.getItem("accessToken");
      const csvText = await fetchResultsData(
        "Timeseries",
        fileObj.name,
        baseUrl,
        storageAccountName,
        simulationName,
        token,
        "handleViewDailySummary",
      );
      const parsed = csvToJson(csvText);
      setPreviewData((prev) => {
        const current = prev[timeKey] || {};
        return {
          ...prev,
          [timeKey]: {
            ...current,
            timeseries: current.timeseries ? null : parsed,
          },
        };
      });
    } catch (err) {
      toast.error("Error viewing timeseries:", err);
      alert(`Error viewing ${fileObj.name}: ${err.message}`);
    }
    setVisibleSeriesKey((prevKey) => (prevKey === timeKey ? null : timeKey));
  };
  return (
    <div className="history-import-container">
      <h3>Simulation History of {simulationName}</h3>
      {error && <p style={{ color: "red" }}>{error}</p>}

      {isLoading ? (
        <div className="spinner-container">
          <div className="spinner"></div>
        </div>
      ) : (
        <>
          <table className="history-import-table">
            <thead>
              <tr>
                <th>Simulation Name</th>
                <th>Time of Generation</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {simulations.map((simGroup) => {
                const simKey = simGroup.simulationName;

                const isSimExpanded = expandedSimulations[simKey];
               

                let actionButton;
                if (simKey === processingSimulationTimeKey) {
                  actionButton = (
                    <button
                      className="action-button"
                      disabled
                      style={uniformButtonStyle}
                    >
                      Processing...
                    </button>
                  );
                } else if (simKey === currentSimulationTimeKey) {
                  actionButton = (
                    <span
                      className="current-views-label"
                      style={{
                        ...uniformButtonStyle,
                        backgroundColor: "#e8f5e9",
                        color: "#2e7d32",
                        padding: "8px 16px",
                        borderRadius: "4px",
                        border: "1px solid #2e7d32",
                        fontWeight: "500",
                      }}
                    >
                      Current Views
                    </span>
                  );
                } else {
                  actionButton = (
                    <button
                      className="action-button generate-views"
                      onClick={() => {
                        setProcessingSimulationTimeKey(simKey);
                        handleGenerateViews(simKey);
                      }}
                      style={uniformButtonStyle}
                    >
                      Generate Views
                    </button>
                  );
                }

                return (
                  <React.Fragment key={simKey}>
                    {/* SIMULATION ROW */}
                    <tr
                      className="simulation-row"
                      style={{ fontWeight: "bold", backgroundColor: "#f5f5f5" }}
                    >
                      <td colSpan="3">
                        <span
                          onClick={() => toggleSimulation(simKey)}
                          style={{ cursor: "pointer" }}
                        >
                          {isSimExpanded ? "▼" : "▶"}{" "}
                          {simKey.replace(`${customer}-`, "")}
                        </span>
                      </td>
                    </tr>

                    {/* METER ROWS */}
                    {isSimExpanded &&
                      simGroup.meters.map((meter) => {
                        const meterKey = `${simKey}-${meter.meterName}`;
                        const isMeterExpanded = expandedMeters[meterKey];

                        return (
                          <React.Fragment key={meterKey}>
                            {/* METER NAME */}
                            <tr style={{ backgroundColor: "#ffffff" }}>
                              <td colSpan="3" style={{ paddingLeft: "2em" }}>
                                <span
                                  onClick={() =>
                                    toggleMeter(simKey, meter.meterName)
                                  }
                                  style={{ cursor: "pointer" }}
                                >
                                  {isMeterExpanded ? "▼" : "▶"}{" "}
                                  {meter.meterName}
                                </span>
                              </td>
                            </tr>

                            {/* FILES (TIMESERIES + DAILY SUMMARY) */}
                            {isMeterExpanded &&
                              meter.files.map((file) => (
                                <React.Fragment key={file.timeKey}>
                                  {/* TIMESERIES ROW */}
                                  <tr style={{ backgroundColor: "#f9f9f9" }}>
                                    <td style={{ paddingLeft: "4em" }}>
                                      Timeseries:{" "}
                                      <b>
                                        {file.timeseries.name.replace(
                                          `${meter.meterName}-`,
                                          "",
                                        )}
                                      </b>
                                    </td>
                                    <td>
                                      {new Date(
                                        file.timeseries.created,
                                      ).toLocaleString()}
                                    </td>
                                    <td>
                                      <button
                                        onClick={() =>
                                          handleViewTimeseries(
                                            file.timeseries,
                                            file.timeKey,
                                            simKey,
                                          )
                                        }
                                      >
                                        View
                                      </button>
                                      <button
                                        onClick={() =>
                                          handleDownloadHistory(
                                            file.timeseries,
                                            simKey,
                                          )
                                        }
                                      >
                                        Download
                                      </button>
                                      <button
                                        onClick={() =>
                                          handleGenerateViews(
                                            file.timeseries,
                                            simKey,
                                          )
                                        }
                                      >
                                        Generate Views
                                      </button>
                                    </td>
                                  </tr>

                                  {/* DAILY SUMMARY ROW */}
                                  <tr style={{ backgroundColor: "#fdfdfd" }}>
                                    <td style={{ paddingLeft: "5em" }}>
                                      Daily Summary:{" "}
                                      <i>{file.dailySummary.name}</i>
                                    </td>
                                    <td>
                                      {new Date(
                                        file.dailySummary.created,
                                      ).toLocaleString()}
                                    </td>
                                    <td>
                                      <button
                                        onClick={() =>
                                          handleViewDailySummary(
                                            file.dailySummary,
                                            file.timeKey,
                                            simKey,
                                          )
                                        }
                                      >
                                        View
                                      </button>
                                      <button
                                        onClick={() =>
                                          handleDownloadHistory(
                                            file.dailySummary,
                                            simKey,
                                          )
                                        }
                                      >
                                        Download
                                      </button>
                                    </td>
                                  </tr>
                                </React.Fragment>
                              ))}
                          </React.Fragment>
                        );
                      })}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>

          
          
        </>
      )}
    </div>
  );
}
